import { useEffect, useState } from 'react'
import { adminApi, getApiErrorMessage } from '../services/adminApi'
import { DataTable } from '../components/DataTable'
import { Alert, Button, Card, PageHeader, Select, Spinner, StatCard } from '../components/ui'
import { formatCurrency, formatDate } from '../utils/format'

export default function Billing() {
  const [tables, setTables] = useState([])
  const [priceTableId, setPriceTableId] = useState('')
  const [report, setReport] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    adminApi.listPrices().then(({ data }) => {
      const list = data.data || []
      setTables(list)
      if (list[0]) setPriceTableId(String(list[0].id))
    })
  }, [])

  const generate = async () => {
    if (!priceTableId) return
    setLoading(true)
    setError('')
    try {
      const { data } = await adminApi.billingReport({ priceTableId })
      setReport(data.data || data)
    } catch (err) {
      setError(getApiErrorMessage(err))
    } finally {
      setLoading(false)
    }
  }

  const revendas = report?.revendas || []
  const totalGeral = revendas.reduce((acc, r) => acc + (Number(r.total_to_pay) || 0), 0)

  return (
    <div className="space-y-6">
      <PageHeader eyebrow="Financeiro" title="Faturamento" description="Relatório consolidado por revenda com base na tabela de preços selecionada." icon="fa-file-invoice-dollar" />

      {error && <Alert type="error">{error}</Alert>}

      <Card title="Gerar relatório" description="Selecione a tabela usada no cálculo deste consolidado.">
        <div className="flex flex-wrap items-end gap-3">
          <Select
            label="Tabela de preços"
            value={priceTableId}
            onChange={(e) => setPriceTableId(e.target.value)}
            className="min-w-[240px]"
          >
            {tables.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </Select>
          <Button onClick={generate} disabled={loading || !priceTableId}>
            {loading ? 'Gerando…' : 'Gerar relatório'}
          </Button>
        </div>
      </Card>

      {loading && <Spinner />}

      {report && !loading && (
        <>
          <div className="grid gap-4 sm:grid-cols-3">
            <StatCard label="Tabela" value={report.table_name} icon="fa-tags" />
            <StatCard label="Gerado em" value={formatDate(report.generated_at)} icon="fa-calendar" tone="cyan" />
            <StatCard label="Total geral" value={formatCurrency(totalGeral)} icon="fa-coins" tone="green" />
          </div>

          <Card title="Por revenda">
            <DataTable
              columns={[
                { key: 'revenda_name', label: 'Revenda' },
                { key: 'qty_pessoas', label: 'Pessoas ativas' },
                { key: 'qty_equipamentos', label: 'Equipamentos' },
                { key: 'qty_clube', label: 'Clubes ativos' },
                {
                  key: 'total_to_pay',
                  label: 'Valor',
                  render: (r) => (
                    <span className="font-semibold text-slate-900">
                      {formatCurrency(r.total_to_pay)}
                    </span>
                  ),
                },
                {
                  key: 'breakdown',
                  label: 'Detalhamento',
                  render: (r) =>
                    r.breakdown?.length ? (
                      <ul className="space-y-1 text-xs text-slate-600">
                        {r.breakdown.map((b, i) => (
                          <li key={i}>
                            {b.description}: {formatCurrency(b.value)}
                          </li>
                        ))}
                      </ul>
                    ) : (
                      '—'
                    ),
                },
              ]}
              rows={revendas}
              emptyMessage="Nenhuma revenda com dados."
            />
          </Card>
        </>
      )}
    </div>
  )
}
